// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AvatarInput } from '@/components/avatar-input';
import { useFileURL } from '@/hooks/use-file-url';
import { AvatarProps } from '@mui/material';
import { PropsWithChildren } from 'react';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

interface Props<Values extends FieldValues> extends UseControllerProps<Values> {
  /**
   * A react-hook-form control object used to connect the input element to the
   * form.
   */
  control: Control<Values>;

  /**
   * The default source of the avatar image.
   */
  defaultSource?: string;

  /**
   * The system prop that allows defining system overrides as well as additional
   * CSS styles.
   */
  sx?: AvatarProps['sx'];
}

/**
 * A controlled avatar field component that integrates with react-hook-form.
 */
export function FormAvatarInput<Values extends FieldValues>(
  props: PropsWithChildren<Props<Values>>
) {
  const { field } = useController({ name: props.name, control: props.control });
  const fileURL = useFileURL(field.value);

  const source = fileURL ?? props.defaultSource;

  return (
    <AvatarInput source={source} onChange={field.onChange} sx={props.sx}>
      {props.children}
    </AvatarInput>
  );
}
